import { match } from 'ts-pattern'

import type {
  ServiceGetAllResponse,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import {
  toBbpsipBpsipPenyuluhList,
  toBppsdmpPenyuluhList,
  toCityPenyuluhList,
  toDistrictPenyuluhList,
  toProvincePenyuluhList
} from './dto'
import type {
  BbpsipBpsipPenyuluh,
  BppsdmpPenyuluh,
  CityPenyuluh,
  DistrictPenyuluh,
  Penyuluh,
  ProvincePenyuluh
} from './types'

const PENYULUH_REQUEST_TIMEOUT_MS = 30_000
const PENYULUH_QUERY_RETRY = 3

const PENYULUH_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['all_penyuluh', key] as const

type PenyuluhListItem =
  | Penyuluh
  | BppsdmpPenyuluh
  | BbpsipBpsipPenyuluh
  | ProvincePenyuluh
  | CityPenyuluh
  | DistrictPenyuluh

const getAllPenyuluh = (params: {
  type: 'bppsdmp' | 'bbpsip-bpsip' | 'province' | 'city' | 'district'
  instansi_id?: string
  penyuluh_type_id?: string
  penyuluh_status_id?: string
  penyuluh_position_id?: string
  gender_id?: string
  skill_id?: string
  education_id?: string
  search?: string
  agency_id?: string
  province_code?: string
  city_code?: string
  district_code?: string
  bpp_id?: string
  page?: number
  per_page?: number
}): ServiceQueryOptionType<ServiceGetAllResponse<PenyuluhListItem[]>> => ({
  retry: PENYULUH_QUERY_RETRY,
  queryKey: PENYULUH_QUERY_KEY(params),
  queryFn: async (): Promise<ServiceGetAllResponse<PenyuluhListItem[]>> => {
    try {
      const type = params.type ?? 'bppsdmp'

      const resource = match(type)
        .with('bppsdmp', () => 'list/bppsdmp')
        .with('bbpsip-bpsip', () => 'list/bbrmp')
        .with('province', () => 'list/province')
        .with('city', () => `list/province/city`)
        .with('district', () => `list/province/city/kecamatan`)
        .otherwise(() => '')

      const res = await api.get<ServiceGetAllResponse<PenyuluhListItem[]>>(
        `/report/extension-personnel/${resource}`,
        {
          timeout: PENYULUH_REQUEST_TIMEOUT_MS,
          params: {
            type: params.penyuluh_type_id,
            status: params.penyuluh_status_id,
            position: params.penyuluh_position_id,
            gender: params.gender_id,
            skill: params.skill_id,
            education: params.education_id,
            search:
              (params.search ?? '').length > 2 ? params.search : undefined,
            institution: params.agency_id,
            instansi_id: params.instansi_id,
            province_code: params.province_code,
            city_code: params.city_code,
            district_code: params.district_code,
            bpp: params.bpp_id,
            page: params.page,
            per_page: params.per_page
          }
        }
      )

      const rawData = res.data?.data as unknown[] | undefined
      const data = match(type)
        .with('bppsdmp', () => toBppsdmpPenyuluhList(rawData))
        .with('bbpsip-bpsip', () => toBbpsipBpsipPenyuluhList(rawData))
        .with('province', () => toProvincePenyuluhList(rawData))
        .with('city', () => toCityPenyuluhList(rawData))
        .with('district', () => toDistrictPenyuluhList(rawData))
        .otherwise(() => (res.data?.data as Penyuluh[] | undefined) ?? [])

      return {
        data,
        message: res.data?.message ?? '',
        meta: res.data?.meta
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const ktgWorker = {
  getAll: getAllPenyuluh
}
